import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Language = 'en' | 'ar' | 'fr' | 'es';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => any;
  getArray: (key: string) => any[];
  isRTL: boolean;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('ar');
  const [translations, setTranslations] = useState<any>({});
  const [isLoading, setIsLoading] = useState(true);

  const isRTL = language === 'ar';

  useEffect(() => {
    // Load saved language from localStorage
    const savedLanguage = localStorage.getItem('language') as Language;
    if (savedLanguage && ['en', 'ar', 'fr', 'es'].includes(savedLanguage)) {
      setLanguageState(savedLanguage);
    } else {
        // Default to Arabic if no saved preference
        setLanguageState('ar');
        document.documentElement.dir = 'rtl';
        document.documentElement.lang = 'ar';
    }
  }, []);

  useEffect(() => {
    // Load translations for current language
    const loadTranslations = async () => {
      try {
        setIsLoading(true);
        console.log('Loading translations for:', language);
        const langData = await import(`../i18n/${language}.json`);
        setTranslations(langData.default || langData);
        console.log('Translations loaded successfully for:', language);
      } catch (error) {
        console.error(`Failed to load ${language} translations:`, error);
        // Fallback to English
        const enData = await import(`../i18n/en.json`);
        setTranslations(enData.default || enData);
      } finally {
        setIsLoading(false);
      }
    };

    loadTranslations();
  }, [language]);

  const setLanguage = (lang: Language) => {
    console.log('LanguageContext: Setting language to', lang);
    setLanguageState(lang);
    localStorage.setItem('language', lang);
    
    // Set document direction for RTL languages
    if (lang === 'ar') {
      document.documentElement.dir = 'rtl';
      document.documentElement.lang = 'ar';
    } else {
      document.documentElement.dir = 'ltr';
      document.documentElement.lang = lang;
    }
    
    // Force a re-render by updating the body class
    document.body.className = document.body.className.replace(/lang-\w+/g, '') + ` lang-${lang}`;
  };

  const t = (key: string): any => {
    const keys = key.split('.');
    let value: any = translations;
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return key; // Return key if translation not found
      }
    }
    
    return value; // Return the actual value (could be string, array, or object)
  };

  // Helper function to safely get arrays
  const getArray = (key: string): any[] => {
    const value = t(key);
    return Array.isArray(value) ? value : [];
  };

  useEffect(() => {
    // Set initial document direction
    if (language === 'ar') {
      document.documentElement.dir = 'rtl';
      document.documentElement.lang = 'ar';
    } else {
      document.documentElement.dir = 'ltr';
      document.documentElement.lang = language;
    }
  }, [language]);

  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
    getArray,
    isRTL,
    isLoading,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
